
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Heading,
    Text,
    VStack,
    SimpleGrid,
    Card,
    CardBody,
    Stat,
    StatLabel,
    StatNumber,
    StatHelpText,
    StatArrow,
    Select,
    Flex,
    useColorModeValue,
    HStack,
    Icon,
    Skeleton
} from '@chakra-ui/react';
import { CalendarIcon } from '@chakra-ui/icons';
import { useUser } from '../Context/userContext';
import { useAuthContext } from '../contexts/AuthContext';
import { apiJson } from '../lib/api';
import { AccountSelector } from '../components/common/AccountSelector';
import { CashFlowChart, CashFlowMonth } from '../components/dashboard/CashFlowChart';

interface YearKpis {
    totalIncome: number;
    totalExpenses: number;
    netResult: number;
    transactionCount: number;
}

/**
 * Page to display a yearly summary of tenant transactions.
 * KPIs (income, expenses, net) are computed client-side using the same
 * formula as XactoPivot: accountingAmount = -amount (Plaid inversion).
 * This guarantees the Net Result here equals the pivot table's grand total
 * when both are filtered to the same year and accounts.
 */
export const TenantTransactionsSummaryPage: React.FC = () => {
    const { selectedTenant } = useUser();
    const { session } = useAuthContext();
    const sessionRef = useRef(session);
    useEffect(() => { sessionRef.current = session; });
    const hasSession = !!session?.access_token;

    const [availableYears, setAvailableYears] = useState<number[]>([]);
    const [kpis, setKpis] = useState<YearKpis | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
    const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
    const [monthlyChartData, setMonthlyChartData] = useState<CashFlowMonth[]>([]);
    const [isChartLoading, setIsChartLoading] = useState(false);

    // Fetch the list of years that have transactions (lightweight backend call)
    const fetchAvailableYears = useCallback(async () => {
        if (!sessionRef.current?.access_token || !selectedTenant) return;
        try {
            const data = await apiJson<{ years: { year: number }[] }>(
                '/api/plaid-transactions/summary',
                { headers: { 'x-tenant-id': selectedTenant.id } },
                sessionRef.current!.access_token,
            );
            const years = (data.years ?? []).map((y: { year: number }) => y.year).sort((a, b) => b - a);
            setAvailableYears(years);
            if (years.length > 0 && !years.includes(selectedYear)) {
                setSelectedYear(years[0]);
            }
        } catch {
            setAvailableYears([new Date().getFullYear()]);
        }
    }, [hasSession, selectedTenant?.id]);

    // Fetch raw transactions for the selected year and compute KPIs using the
    // same formula as useXactoMatrix (accountingAmount = -amount), so the Net
    // Result here always equals the XactoPivot grand total for the same period.
    const fetchKpis = useCallback(async () => {
        if (!sessionRef.current?.access_token || !selectedTenant) return;
        setIsLoading(true);
        try {
            const startDate = `${selectedYear}-01-01`;
            const endDate   = `${selectedYear}-12-31`;
            const PAGE_SIZE = 500;
            let allTransactions: { amount: number }[] = [];
            let offset = 0;
            let hasMore = true;

            while (hasMore) {
                const params = new URLSearchParams();
                params.append('startDate', startDate);
                params.append('endDate', endDate);
                params.append('limit', PAGE_SIZE.toString());
                params.append('offset', offset.toString());
                params.append('includePending', 'false');
                if (selectedAccountIds.length > 0) {
                    params.append('accountIds', selectedAccountIds.join(','));
                }

                const result = await apiJson<{ transactions: { amount: number }[]; hasMore: boolean }>(
                    `/api/plaid-transactions?${params.toString()}`,
                    { headers: { 'x-tenant-id': selectedTenant.id } },
                    sessionRef.current!.access_token,
                );

                allTransactions = allTransactions.concat(result.transactions ?? []);
                hasMore = result.hasMore;
                offset += result.transactions.length;
            }

            // Same formula as useXactoMatrix line 72: accountingAmount = -amount
            // income  = sum of accountingAmount where accountingAmount > 0
            // expenses = sum of |accountingAmount| where accountingAmount < 0
            // net     = income - expenses = SUM(-amount) = pivot grandTotal
            let income = 0;
            let expenses = 0;
            for (const t of allTransactions) {
                const accountingAmount = -(t.amount || 0);
                if (accountingAmount > 0) income   += accountingAmount;
                else if (accountingAmount < 0) expenses += Math.abs(accountingAmount);
            }

            setKpis({
                totalIncome:      income,
                totalExpenses:    expenses,
                netResult:        income - expenses,
                transactionCount: allTransactions.length,
            });
        } catch (err) {
            console.error('Failed to fetch summary KPIs:', err);
            setKpis(null);
        } finally {
            setIsLoading(false);
        }
    }, [hasSession, selectedTenant?.id, selectedYear, selectedAccountIds]);

    const fetchMonthlyChart = useCallback(async () => {
        if (!sessionRef.current?.access_token || !selectedTenant) return;
        setIsChartLoading(true);
        try {
            const params = new URLSearchParams({ year: String(selectedYear) });
            if (selectedAccountIds.length > 0) {
                selectedAccountIds.forEach((id) => params.append('accountIds', id));
            }
            const data = await apiJson<{ months: CashFlowMonth[] }>(
                `/api/plaid-transactions/monthly-chart?${params.toString()}`,
                { headers: { 'x-tenant-id': selectedTenant.id } },
                sessionRef.current!.access_token,
            );
            setMonthlyChartData(data.months);
        } catch {
            setMonthlyChartData([]);
        } finally {
            setIsChartLoading(false);
        }
    }, [hasSession, selectedTenant?.id, selectedYear, selectedAccountIds]);

    useEffect(() => { fetchAvailableYears(); }, [fetchAvailableYears]);
    useEffect(() => { fetchKpis();           }, [fetchKpis]);
    useEffect(() => { fetchMonthlyChart();   }, [fetchMonthlyChart]);

    const totalIncome   = kpis?.totalIncome   ?? 0;
    const totalExpenses = kpis?.totalExpenses ?? 0;
    const netResult     = kpis?.netResult     ?? 0;
    const hasData       = kpis !== null;

    const formatCurrency = (val: number) =>
        new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);

    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const headerBg    = useColorModeValue('white', 'gray.800');
    // Hoisted out of the JSX below: it used to be called inline on the year
    // <Select>, which sits *after* the two early returns in this component.
    // That made the hook run on some renders and not others, so React's hook
    // order changed between renders ("React has detected a change in the
    // order of Hooks called by TenantTransactionsSummaryPage").
    const selectBg    = useColorModeValue('white', 'gray.700');

    if (isLoading && !hasData) {
        return <Box p={6}><Text>Loading transactions...</Text></Box>;
    }

    if (!hasData) {
        return (
            <Box p={6}>
                <VStack spacing={6} align="stretch">
                    <Flex
                        justify="space-between"
                        align="center"
                        p={4}
                        bg={headerBg}
                        borderRadius="lg"
                        borderWidth="1px"
                        borderColor={borderColor}
                        shadow="sm"
                    >
                        <Heading size="lg" color="text.primary">Transactions Summary</Heading>
                    </Flex>
                    <Box
                        p={8}
                        textAlign="center"
                        borderWidth="1px"
                        borderStyle="dashed"
                        borderRadius="lg"
                        color="gray.500"
                    >
                        <Heading size="md" mb={2}>No Transactions Found</Heading>
                        <Text>Load transactions or connect a bank account to view the summary.</Text>
                    </Box>
                </VStack>
            </Box>
        );
    }

    return (
        <Box p={6} overflow="visible">
            <VStack spacing={6} align="stretch" overflow="visible">
                {/* Header */}
                <Flex
                    justify="space-between"
                    align="flex-start"
                    p={4}
                    bg={headerBg}
                    borderRadius="lg"
                    borderWidth="1px"
                    borderColor={borderColor}
                    shadow="sm"
                    wrap="wrap"
                    gap={4}
                    overflow="visible"
                    position="relative"
                    minH="auto"
                >
                    <Box>
                        <Heading size="lg" color="text.primary">Transactions Summary</Heading>
                        <Text color="text.secondary" mt={1}>
                            Annual financial overview of original tenant transactions.
                        </Text>
                    </Box>

                    <HStack spacing={4}>
                        <AccountSelector
                            values={selectedAccountIds}
                            onChangeMulti={setSelectedAccountIds}
                            multiSelect
                            compact
                            size="sm"
                        />
                        <HStack>
                            <Icon as={CalendarIcon} color="gray.500" />
                            <Select
                                width="100px"
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(Number(e.target.value))}
                                bg={selectBg}
                            >
                                {(availableYears.length > 0 ? availableYears : [selectedYear]).map(year => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </Select>
                        </HStack>
                    </HStack>
                </Flex>

                {/* KPI Cards */}
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                    <Card variant="outline">
                        <CardBody>
                            <Stat>
                                <StatLabel>Total Income</StatLabel>
                                <Skeleton isLoaded={!isLoading} height={isLoading ? '32px' : undefined} mb={isLoading ? 2 : 0}>
                                    <StatNumber color="green.500">{formatCurrency(totalIncome)}</StatNumber>
                                </Skeleton>
                                <Skeleton isLoaded={!isLoading} height={isLoading ? '16px' : undefined} width={isLoading ? '140px' : undefined}>
                                    <StatHelpText>
                                        {kpis?.transactionCount ?? 0} transactions in {selectedYear}
                                    </StatHelpText>
                                </Skeleton>
                            </Stat>
                        </CardBody>
                    </Card>
                    <Card variant="outline">
                        <CardBody>
                            <Stat>
                                <StatLabel>Total Expenses</StatLabel>
                                <Skeleton isLoaded={!isLoading} height={isLoading ? '32px' : undefined} mb={isLoading ? 2 : 0}>
                                    <StatNumber color="red.500">{formatCurrency(-totalExpenses)}</StatNumber>
                                </Skeleton>
                                <Skeleton isLoaded={!isLoading} height={isLoading ? '16px' : undefined} width={isLoading ? '80px' : undefined}>
                                    <StatHelpText>For {selectedYear}</StatHelpText>
                                </Skeleton>
                            </Stat>
                        </CardBody>
                    </Card>
                    <Card variant="outline">
                        <CardBody>
                            <Stat>
                                <StatLabel>Net Result</StatLabel>
                                <Skeleton isLoaded={!isLoading} height={isLoading ? '32px' : undefined} mb={isLoading ? 2 : 0}>
                                    <StatNumber color={netResult >= 0 ? 'green.600' : 'red.600'}>
                                        {formatCurrency(netResult)}
                                    </StatNumber>
                                </Skeleton>
                                <Skeleton isLoaded={!isLoading} height={isLoading ? '16px' : undefined} width={isLoading ? '80px' : undefined}>
                                    <StatHelpText>
                                        <StatArrow type={netResult >= 0 ? 'increase' : 'decrease'} />
                                        {netResult >= 0 ? 'Surplus' : 'Deficit'}
                                    </StatHelpText>
                                </Skeleton>
                            </Stat>
                        </CardBody>
                    </Card>
                </SimpleGrid>

                {/* Cash Flow Chart */}
                <Card variant="outline">
                    <CardBody>
                        <Heading size="sm" mb={4} color="text.secondary">
                            Balance Flow — {selectedYear}
                        </Heading>
                        <Skeleton isLoaded={!isChartLoading} borderRadius="md">
                            <CashFlowChart months={monthlyChartData} />
                        </Skeleton>
                    </CardBody>
                </Card>
            </VStack>
        </Box>
    );
};
